import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header with hamburger menu, logo, and login */}
      <header className="flex justify-between items-center p-6 lg:p-8 relative z-50">
        <div className="flex items-center space-x-4">
          {/* Hamburger Menu Button - visible on all screen sizes */}
          <button
            onClick={toggleMenu}
            className="p-2 rounded-md text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>

          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            Vegetablah
          </div>
        </div>
      </header>

      {/* Navigation Menu Overlay */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-40" onClick={toggleMenu}>
          <div 
            className="absolute top-0 left-0 w-64 h-dvh bg-surface-card dark:bg-surface-dark shadow-lg transform transition-transform duration-300 ease-in-out"
            onClick={(e) => e.stopPropagation()}
          >
            <nav className="flex flex-col h-full">
              <ul className="flex-1 p-6 pt-20 space-y-2">
                <li>
                  <Link
                    to="/features/health-twin"
                    className="block px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    onClick={toggleMenu}
                  >
                    How it works
                  </Link>
                </li>
                <li>
                  <Link
                    to="/features/predictions"
                    className="block px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    onClick={toggleMenu}
                  >
                    Why trust it
                  </Link>
                </li>
                <li>
                  <Link
                    to="/articles"
                    className="block px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    onClick={toggleMenu}
                  >
                    Articles
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about"
                    className="block px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    onClick={toggleMenu}
                  >
                    About
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="block px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    onClick={toggleMenu}
                  >
                    Contact
                  </Link>
                </li>
              </ul>
              
              <div className="p-6">
                <div className="flex flex-col items-start">
                  <Link
                    to="/login"
                    className="inline-block w-24 bg-gray-700 text-white px-4 py-2 rounded-full hover:bg-gray-800 transition-colors font-medium text-center mb-2"
                    onClick={toggleMenu}
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="inline-block w-24 bg-gray-700 text-white px-4 py-2 rounded-full hover:bg-gray-800 transition-colors font-medium text-center mb-3"
                    onClick={toggleMenu}
                  >
                    Sign Up
                  </Link>
                </div>
              </div>
            </nav>
          </div>
        </div>
      )}

      {/* Main hero section */}
      <main className="flex-1 flex items-center justify-center px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          {/* Headline */}
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight break-words">
            Optimize Your Health with AI-Powered Guidance
          </h1>

          {/* Feature Icons */}
          <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-5 mb-8">
            <div className="flex flex-col items-center space-y-2">
              <div className="p-3 bg-green-100 dark:bg-green-900 rounded-full">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-400">Track Data</span>
            </div>

            <div className="flex flex-col items-center space-y-2">
              <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-full">
                <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-400">AI Insights</span>
            </div>

            <div className="flex flex-col items-center space-y-2">
              <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-full">
                <svg className="w-8 h-8 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-400">Connect Devices</span>
            </div>

            <div className="flex flex-col items-center space-y-2">
              <div className="p-3 bg-red-100 dark:bg-red-900 rounded-full">
                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-400">Health Goals</span>
            </div>
          </div>

          {/* Subheadline */}
          <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 dark:text-gray-300 mb-12 max-w-2xl mx-auto leading-relaxed">
            Connect your fitness devices, share your goals, and get machine learning-driven recommendations tailored just for you. Start feeling better today.
          </p>

          {/* CTA Button */}
          <Link
            to="/register"
            className="inline-block max-w-full bg-green-600 hover:bg-green-700 text-white text-lg sm:text-xl font-semibold py-4 px-8 sm:px-12 rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300"
          >
            Connect Your Device Now
          </Link>
        </div>
      </main>
    </div>
  );
};

export default LandingPage;
